/**
 * Double-booking and working-hours checks for the admin schedule.
 *
 * Pure (no Supabase / React) so it can be unit-tested directly
 * (tests/schedule-clash-lib.spec.ts). The hook in src/hooks/useScheduleClashCheck
 * loads the data; the Schedule, Team calendar and Enquiries screens show the
 * result in one shared "Book anyway / Pick another time" prompt.
 *
 * The double-booking rule matches the calendar's red overlap markers and the
 * SQL in recurring_visit_clash(): two non-cancelled jobs clash when they share
 * a cleaner (job_assignments first, legacy team_member_id as fallback), fall on
 * the same date and overlap in time. On top of that we respect the business's
 * buffer between jobs (company_info.buffer_minutes), so a job that starts five
 * minutes after another ends is flagged when the owner wants a 30-minute gap.
 */

export interface ClashJob {
  id: string;
  title: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  status: string;
  address?: string | null;
  team_member_id: string | null;
  job_assignments?: Array<{ team_member_id: string | null }> | null;
}

export interface ClashMember {
  id: string;
  name: string;
}

/** A row of team_availability: a cleaner's usual hours for one weekday (0 = Sunday). */
export interface AvailabilitySlot {
  team_member_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_available: boolean;
}

/** A row of team_schedule_overrides: time off (is_blocked) or one-off hours on a date. */
export interface ScheduleOverride {
  team_member_id: string;
  override_date: string;
  start_time: string | null;
  end_time: string | null;
  is_blocked: boolean;
  reason?: string | null;
}

/** A row of business_hours (0 = Sunday). */
export interface OpeningHours {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

export interface ClashCandidate {
  /** yyyy-MM-dd */
  date: string;
  /** HH:mm or HH:mm:ss */
  start: string;
  end: string;
  /** Cleaners who would be on this job. Empty = unassigned (only opening hours are checked). */
  memberIds: string[];
  /** The job being edited, so it never clashes with itself. */
  excludeJobId?: string | null;
}

export interface ClashContext {
  jobs: ClashJob[];
  members: ClashMember[];
  /** Minimum gap between a cleaner's jobs. 0 / null = back-to-back is fine. */
  bufferMinutes?: number | null;
  availability?: AvailabilitySlot[];
  overrides?: ScheduleOverride[];
  openingHours?: OpeningHours[];
}

export type ScheduleWarningKind = 'double_booking' | 'buffer' | 'time_off' | 'outside_availability' | 'outside_opening_hours';

export interface ScheduleWarning {
  kind: ScheduleWarningKind;
  /** Plain-English line for the prompt, naming the cleaner and the clash. */
  message: string;
  memberId?: string;
  jobId?: string;
}

const DAY_NAMES = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "09:30" / "09:30:00" → minutes after midnight. */
export const toMinutes = (time: string): number => {
  const [h, m] = time.split(':');
  return parseInt(h, 10) * 60 + (parseInt(m, 10) || 0);
};

const hhmm = (time: string) => time.slice(0, 5);

/** Weekday (0 = Sunday) of a yyyy-MM-dd date, independent of the browser's time zone. */
export const weekdayOfDate = (date: string): number => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

/** "Mon 3 Nov" */
export const shortDate = (date: string): string => {
  const [, m, d] = date.split('-').map(Number);
  return `${SHORT_DAYS[weekdayOfDate(date)]} ${d} ${MONTHS[m - 1]}`;
};

/** First line of an address, e.g. "14 Park Rd, Leeds LS1" → "14 Park Rd". */
export const shortAddress = (address: string | null | undefined): string | null => {
  const first = (address ?? '').split(',')[0]?.trim();
  return first ? first : null;
};

/** Cleaners on a job: job_assignments are authoritative, legacy team_member_id is the fallback. */
export const assignedMemberIds = (job: Pick<ClashJob, 'team_member_id' | 'job_assignments'>): string[] => {
  const ids = (job.job_assignments ?? [])
    .map((a) => a.team_member_id)
    .filter((id): id is string => Boolean(id));
  if (ids.length === 0 && job.team_member_id) return [job.team_member_id];
  return Array.from(new Set(ids));
};

const describeJob = (job: ClashJob) => {
  const where = shortAddress(job.address);
  return `${job.title} ${hhmm(job.start_time)}–${hhmm(job.end_time)}${where ? ` at ${where}` : ''}`;
};

/**
 * Everything worth warning about before booking `candidate`. Empty means it's
 * clear. Double bookings come first, then gaps shorter than the buffer, then
 * time off / availability, then opening hours.
 */
export function findScheduleWarnings(candidate: ClashCandidate, ctx: ClashContext): ScheduleWarning[] {
  const start = toMinutes(candidate.start);
  const end = toMinutes(candidate.end);
  if (!(end > start)) return [];

  const buffer = Math.max(0, Math.floor(Number(ctx.bufferMinutes) || 0));
  const nameOf = new Map(ctx.members.map((m) => [m.id, m.name]));
  const name = (id: string) => nameOf.get(id) ?? 'This cleaner';
  const members = Array.from(new Set(candidate.memberIds.filter(Boolean)));
  const dow = weekdayOfDate(candidate.date);

  const clashes: ScheduleWarning[] = [];
  const tight: ScheduleWarning[] = [];
  const hours: ScheduleWarning[] = [];

  const sameDay = ctx.jobs
    .filter(
      (j) =>
        j.scheduled_date === candidate.date &&
        j.status !== 'cancelled' &&
        j.id !== candidate.excludeJobId,
    )
    .sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time));

  for (const memberId of members) {
    for (const job of sameDay) {
      if (!assignedMemberIds(job).includes(memberId)) continue;
      const jobStart = toMinutes(job.start_time);
      const jobEnd = toMinutes(job.end_time);
      if (start < jobEnd && end > jobStart) {
        clashes.push({
          kind: 'double_booking',
          memberId,
          jobId: job.id,
          message: `${name(memberId)} already has ${describeJob(job)}`,
        });
      } else if (buffer > 0 && start < jobEnd + buffer && end + buffer > jobStart) {
        const gap = start >= jobEnd ? start - jobEnd : jobStart - end;
        tight.push({
          kind: 'buffer',
          memberId,
          jobId: job.id,
          message: `${name(memberId)} has ${describeJob(job)} — ${gap === 0 ? 'back to back' : `only ${gap} min between jobs`} (you've set ${buffer} min)`,
        });
      }
    }

    // Time off and one-off hours beat the usual weekly pattern.
    const override = (ctx.overrides ?? []).find(
      (o) => o.team_member_id === memberId && o.override_date === candidate.date,
    );
    if (override) {
      if (override.is_blocked) {
        const why = override.reason?.trim();
        hours.push({
          kind: 'time_off',
          memberId,
          message: `${name(memberId)} is off on ${shortDate(candidate.date)}${why ? ` (${why})` : ''}`,
        });
      } else if (override.start_time && override.end_time) {
        if (start < toMinutes(override.start_time) || end > toMinutes(override.end_time)) {
          hours.push({
            kind: 'outside_availability',
            memberId,
            message: `${name(memberId)} is only working ${hhmm(override.start_time)}–${hhmm(override.end_time)} on ${shortDate(candidate.date)}`,
          });
        }
      }
      continue;
    }

    // Only judge availability for cleaners who have set it up.
    const pattern = (ctx.availability ?? []).filter((a) => a.team_member_id === memberId);
    if (pattern.length === 0) continue;
    const today = pattern.filter((a) => a.day_of_week === dow && a.is_available);
    if (today.length === 0) {
      hours.push({
        kind: 'outside_availability',
        memberId,
        message: `${name(memberId)} doesn't usually work on ${DAY_NAMES[dow]}`,
      });
      continue;
    }
    const covered = today.some((a) => toMinutes(a.start_time) <= start && toMinutes(a.end_time) >= end);
    if (!covered) {
      const slot = today[0];
      hours.push({
        kind: 'outside_availability',
        memberId,
        message: `${name(memberId)} usually works ${hhmm(slot.start_time)}–${hhmm(slot.end_time)} on ${DAY_NAMES[dow]}`,
      });
    }
  }

  const opening = (ctx.openingHours ?? []).find((h) => h.day_of_week === dow);
  if (opening) {
    if (!opening.is_open) {
      hours.push({ kind: 'outside_opening_hours', message: `You're normally closed on ${DAY_NAMES[dow]}` });
    } else if (start < toMinutes(opening.open_time) || end > toMinutes(opening.close_time)) {
      hours.push({
        kind: 'outside_opening_hours',
        message: `This is outside your opening hours (${hhmm(opening.open_time)}–${hhmm(opening.close_time)} on ${DAY_NAMES[dow]})`,
      });
    }
  }

  return [...clashes, ...tight, ...hours];
}

/** Heading for the prompt: the most serious kind of warning wins. */
export function warningHeadline(warnings: ScheduleWarning[]): string {
  if (warnings.some((w) => w.kind === 'double_booking')) return 'Scheduling conflict';
  if (warnings.some((w) => w.kind === 'buffer')) return 'Tight gap between jobs';
  if (warnings.some((w) => w.kind === 'time_off')) return 'Cleaner is off that day';
  return 'Outside working hours';
}
